import { DateTime } from "luxon"
import { useCallback } from "react"
import { QueryFunctionContext, useQueryClient } from "@tanstack/react-query"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { ResourceItem } from "../../../createOrderTypes"
import { ConfigQuery, fetchEventsForServiceCenter } from "../../../createOrderApi"
import { NullableCalendarEvent, TimelineCalendarEvent } from "../calendar/fullCalendarTypes"
import { useTimelineContext } from "../context/TimelineContextProvider"
import { useResourceBookingDraft } from "../ResourceBookingDraftContext"
import { getEventBackgroundColor } from "./helpers"

type FetchEventsForServiceCenterQueryKey = Readonly<[string, number, string, string, string]>

export type IsResourceAvailableFn = (
    newStart: string,
    newEnd: string,
    resourceIds: string[],
    excludeEventId?: string,
) => boolean

type Options = Readonly<{}>

export function useResourceHelpers({}: Options) {
    const dateTimeFactory = useDateTime()
    const { timelineResources, resourceBufferMap, eventIndexRef, userId, localEventUpdateId } = useTimelineContext()
    const { draftEvent: localEvent, setDraftEvent, fullResources } = useResourceBookingDraft()

    const { mutateAsync: callFetchEvents } = useApiMutation(fetchEventsForServiceCenter, {})
    const queryClient = useQueryClient()

    const loadAndTransformEvents = useCallback(
        async ({ queryKey }: QueryFunctionContext<FetchEventsForServiceCenterQueryKey>) => {
            const [_queryName, userId, serviceCenterId, startDate, endDate] = queryKey

            const eventsResponse = await callFetchEvents({
                userId,
                serviceCenterId,
                startDate,
                endDate,
            })

            return (eventsResponse.events ?? []).reduce<TimelineCalendarEvent[]>((acc, el) => {
                if (localEventUpdateId && el.orderId === localEventUpdateId) {
                    return acc
                }

                acc.push({
                    id: String(el.orderId),
                    resourceIds: el.resourceIds.map(String),
                    start: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(el.startDateTime)),
                    end: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(el.endDateTime)),
                    subject: el?.subject || "",
                    host: el?.host || "",
                    canEdit: !!el?.canEdit,
                    outOfService: !!el?.outOfService,
                    backgroundColor: getEventBackgroundColor({
                        outOfService: !!el?.outOfService,
                        canEdit: !!el?.canEdit,
                    }),
                })

                return acc
            }, [])
        },
        [dateTimeFactory, localEventUpdateId],
    )

    const createSingleDayFetcher = useCallback(
        (serviceCenterId: string, dayOffset: number) =>
            async (
                fetchInfo: { startStr: string; endStr: string },
                successCallback: (events: TimelineCalendarEvent[]) => void,
                failureCallback: (error: any) => void,
            ) => {
                try {
                    const rangeStart = dateTimeFactory.fromISO(fetchInfo.startStr).plus({ days: dayOffset })
                    const hardEnd = dateTimeFactory.fromISO(fetchInfo.endStr)
                    const rangeEnd = DateTime.min(rangeStart.plus({ days: 1 }), hardEnd)

                    const startISO = dateTimeFactory.toApiISOMinutesFormat(rangeStart)
                    const endISO = dateTimeFactory.toApiISOMinutesFormat(rangeEnd)
                    const dayISODate = rangeStart.toISODate()

                    if (!dayISODate) {
                        successCallback([])
                        return
                    }

                    const queryKey: FetchEventsForServiceCenterQueryKey = [
                        ConfigQuery.EVENTS_LIST_BY_DAY,
                        userId,
                        serviceCenterId,
                        startISO,
                        endISO,
                    ] as const

                    const result = await queryClient.fetchQuery({
                        queryKey,
                        staleTime: 60_000,
                        queryFn: loadAndTransformEvents,
                    })

                    for (const event of result) {
                        for (const resId of event.resourceIds) {
                            if (!eventIndexRef.current[resId]) eventIndexRef.current[resId] = []
                            const existing = eventIndexRef.current[resId].filter((e) => e.id !== event.id)
                            eventIndexRef.current[resId] = [...existing, event]
                        }
                    }

                    successCallback(result)
                } catch (err) {
                    failureCallback(err)
                }
            },
        [queryClient, dateTimeFactory, loadAndTransformEvents, userId, localEventUpdateId],
    )

    const handleChangeLocalEvent = useCallback(
        (event: NullableCalendarEvent) => {
            if (!event) {
                setDraftEvent(null)
                return
            }
            const currentFullResources: ResourceItem[] = fullResources ?? []

            const fromTimeline = timelineResources
                .filter((res) => event.resourceIds.includes(res.id))
                .map((res) => ({
                    ...res,
                    id: Number(res.id),
                }))

            const fromPrevious = currentFullResources
                .filter(
                    (res) =>
                        event.resourceIds.includes(res.id.toString()) && !fromTimeline.some((r) => r.id === res.id),
                )
                .map((res) => ({
                    ...res,
                    id: Number(res.id),
                }))

            setDraftEvent(event, [...fromTimeline, ...fromPrevious])
        },
        [fullResources, timelineResources, setDraftEvent],
    )

    const checkOverlap = useCallback(
        (newStart: string, newEnd: string, resourceId: string, excludeEventId?: string): boolean => {
            const buffer = resourceBufferMap[resourceId] || 0

            const bufferedStart = dateTimeFactory.fromISO(newStart).minus({ minutes: buffer })
            const bufferedEnd = dateTimeFactory.fromISO(newEnd).plus({ minutes: buffer })

            const relevantEvents = eventIndexRef.current[resourceId] || []
            const allEvents = [...relevantEvents, ...(localEvent?.resourceIds.includes(resourceId) ? [localEvent] : [])]

            return allEvents.some(({ id, start, end }) => {
                if (excludeEventId && id === excludeEventId) return false

                const eventStart = dateTimeFactory.fromISO(start)
                const eventEnd = dateTimeFactory.fromISO(end)

                const isOverlapping =
                    (bufferedStart < eventEnd && bufferedStart >= eventStart) ||
                    (bufferedEnd > eventStart && bufferedEnd <= eventEnd) ||
                    (bufferedStart <= eventStart && bufferedEnd >= eventEnd)

                return isOverlapping
            })
        },
        [resourceBufferMap, dateTimeFactory],
    )

    const isResourceAvailable: IsResourceAvailableFn = useCallback(
        (newStart, newEnd, resourceIds, excludeEventId) => {
            return !resourceIds.some((resourceId) => checkOverlap(newStart, newEnd, resourceId, excludeEventId))
        },
        [checkOverlap],
    )

    return {
        createSingleDayFetcher,
        handleChangeLocalEvent,
        isResourceAvailable,
    }
}
